/**
 * Application_Form — document-catalog pure logic
 * (application-form-builder spec, task 6.1).
 *
 * This module holds the I/O-free heart of Requirement 20: the director-
 * configurable, per-Competition `Document_Catalog` that REPLACES the previously
 * hardcoded `ParticipantDocuments` arrays. The catalog drives both the uploader
 * dropdown / required checklist (which entries are in scope for an applicant)
 * and the submission gate (which required entries are still unsatisfied). The
 * underlying `DocumentReference` storage is unchanged by this feature
 * (Req 20.4); the catalog only describes the slots and their scope/requirement.
 *
 * It is exercised both by the `application-form-service` Lambda (which loads the
 * configured catalog and the applicant's uploaded `DocumentReference`s) and by
 * `jest` + `fast-check` / the frontend `vitest` suite, exactly like the
 * `division-registry/visibility.ts` and `person-service/personLogic.ts`
 * precedents. There is no AWS SDK and no I/O here — only the rules.
 *
 * What lives here:
 *   - `DocumentCatalogEntry` — one configured slot (label, required-for-submission
 *     flag, optional Division/applicant-type scope) (Req 20.1).
 *   - `validateCatalogEntry` — definition-time validation of a catalog entry.
 *   - `entriesInScope` — the entries whose scope includes a given applicant's
 *     Division + applicant type; drives the dropdown + checklist (Req 20.2).
 *   - `missingRequiredDocuments` — the submission gate: in-scope, required slots
 *     with no matching upload; submission is blocked iff this is non-empty
 *     (Req 20.3).
 */

import { ApplicantType } from '../division-registry/resolution';
import { isAllDivisionsVisibility } from '../division-registry/visibility';

// ---------------------------------------------------------------------------
// Shared validation-error shape
//
// NOTE: The canonical `FieldValidationError` is owned by `fieldValidation.ts`
// (task 1.1). It is mirrored here so this module compiles and is testable on
// its own as a wave-0 sibling task; the two definitions are structurally
// identical and intended to be consolidated onto the `fieldValidation.ts`
// export once both modules have landed. (Same pattern as `formStructure.ts`.)
// ---------------------------------------------------------------------------

/** Every validation-error code used across the application-form pure modules. */
export type FieldValidationErrorCode =
  | 'REQUIRED'
  | 'WRONG_TYPE'
  | 'WORD_LIMIT'
  | 'CHAR_LIMIT'
  | 'EMPTY_OPTIONS'
  | 'NOT_AN_OPTION'
  | 'FILE_TYPE'
  | 'FILE_SIZE'
  | 'FILE_COUNT'
  | 'BAD_EMAIL'
  | 'BAD_PHONE'
  | 'BAD_NUMBER'
  | 'BAD_DATE'
  | 'BOUND_VALUE_NOT_ALLOWED'
  | 'INACTIVE_DIVISION'
  | 'MISSING_REQUIRED_DOCUMENT'
  | 'CONTACT_FIELD_MISSING';

/**
 * A single, field-identifying validation error. `fieldId` names the offending
 * input so a Director sees exactly which element to fix.
 */
export interface FieldValidationError {
  fieldId: string;
  code: FieldValidationErrorCode;
  message: string;
}

// ---------------------------------------------------------------------------
// Types

/**
 * One slot in the per-Competition `Document_Catalog` (Req 20.1).
 *
 *   - `label` — the human-readable name shown in the uploader dropdown and the
 *     required checklist.
 *   - `requiredForSubmission` — whether an in-scope applicant must have a
 *     matching upload before the application may be submitted (Req 20.3).
 *   - `visibleDivisionKeys` — optional Division scope; a null/undefined/empty
 *     selection means "all divisions" (the `BOTH` semantics, mirroring
 *     `visibility.ts#isAllDivisionsVisibility`) (Req 20.1).
 *   - `applicantTypeScope` — optional applicant-type scope; null/undefined or
 *     `'BOTH'` means "all applicant types" (Req 20.1).
 *   - `allowedContentTypes` — optional upload constraint; when present, only the
 *     listed MIME content types are accepted for this slot (apply-document-uploads
 *     Req 5.1). Absent/null means no content-type restriction (Req 5.4).
 *   - `maxFileSizeBytes` — optional upload constraint; when present, a file may
 *     not exceed this many bytes (Req 5.2). Absent/null means no size limit (Req 5.4).
 *   - `maxFileCount` — optional upload constraint; when present, the slot accepts
 *     at most this many uploaded documents (Req 5.3). Absent/null means no count
 *     limit (Req 5.4).
 *   - `documentType` — optional mapping to a known `DocumentType`; when set, a
 *     known slot (e.g. `HEADSHOT`) keeps its legacy behaviors, and when unset the
 *     apply surface sends the generic `APPLICATION_DOCUMENT` type (Decision D2).
 */
export interface DocumentCatalogEntry {
  id: string;
  label: string;
  requiredForSubmission: boolean;
  /** Optional scope: empty/null = all divisions (Req 20.1). */
  visibleDivisionKeys?: string[] | null;
  /** Optional scope: null/'BOTH' = all applicant types (Req 20.1). */
  applicantTypeScope?: ApplicantType | 'BOTH' | null;
  /** Optional upload constraint: accepted MIME types; absent = unrestricted (Req 5.1, 5.4). */
  allowedContentTypes?: string[] | null;
  /** Optional upload constraint: max file size in bytes; absent = unlimited (Req 5.2, 5.4). */
  maxFileSizeBytes?: number | null;
  /** Optional upload constraint: max uploads per slot; absent = unlimited (Req 5.3, 5.4). */
  maxFileCount?: number | null;
  /** Optional mapping to a known DocumentType; unset = generic APPLICATION_DOCUMENT (Decision D2). */
  documentType?: DocumentType | null;
  /**
   * Optional, applicant-facing instructions shown beneath the uploader
   * (application-builder-documents Req 2). Empty/whitespace is normalized to
   * absent by the upsert op, so a present value here is always meaningful.
   */
  helpText?: string | null;
  /**
   * Explicit phase; absent ⇒ derived from `requiredForSubmission` via
   * `effectivePhase` (application-builder-documents Req 3, 6.4).
   */
  phase?: DocumentPhase | null;
  /**
   * Dense, ascending presentation order within a Competition
   * (application-builder-documents Req 4). Absent on legacy rows; a read-order
   * fallback supplies a total order until backfilled.
   */
  displayOrder?: number | null;
}

/**
 * The `DocumentType` GraphQL enum, mirrored here as a pure string union (the
 * same dependency-light precedent as `ApplicantType` in
 * `division-registry/resolution.ts`) so this shared FE/BE module stays I/O-free
 * and does not pull in the backend `types/database` graph. Kept in sync with
 * the `DocumentType` enum in `schema.graphql`; `APPLICATION_DOCUMENT` is the
 * generic catalog-upload type (Decision D2).
 */
export type DocumentType =
  | 'HEADSHOT'
  | 'AD_PAGE'
  | 'PHOTO_PAGE'
  | 'WEDDING_PHOTO'
  | 'MARRIAGE_LICENSE'
  | 'PROOF_OF_RESIDENCY'
  | 'VOLUNTEER_FORM'
  | 'APPLICATION_FEE_RECEIPT'
  | 'DIVORCE_DECREE'
  | 'DEATH_CERTIFICATE'
  | 'APPLICATION_DOCUMENT';

/**
 * The application `Phase` for a `Document_Catalog_Entry`, mirroring the existing
 * `Form_Page.phase` model (application-form-builder Req 16). `PHASE_1` documents
 * must be provided before an applicant can submit; `PHASE_2` documents are
 * provided after overall application approval
 * (application-builder-documents Req 3).
 */
export type DocumentPhase = 'PHASE_1' | 'PHASE_2';

/** One uploaded document, matched to a catalog entry by `catalogEntryId` (Req 20.3). */
export interface UploadedDocument {
  catalogEntryId: string;
}

// ---------------------------------------------------------------------------
// Definition-time validation

/**
 * Validate a single catalog entry's own configuration. An entry must carry a
 * non-empty (post-trim) label so the dropdown / checklist can name it; an empty
 * or whitespace-only label is rejected, identifying the label. Returns `null`
 * when the entry is well-formed. Pure + total.
 */
export function validateCatalogEntry(entry: DocumentCatalogEntry): FieldValidationError | null {
  if (typeof entry.label !== 'string' || entry.label.trim().length === 0) {
    return {
      fieldId: entry.id,
      code: 'REQUIRED',
      message: 'Document catalog entry label is required and must be non-empty.',
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Upload-time file validation (apply-document-uploads Req 5)

/** A candidate file to validate against a catalog entry's upload constraints. */
export interface CandidateUpload {
  /** The file's MIME content type. */
  contentType: string;
  /** The file's size in bytes. */
  sizeBytes: number;
}

/**
 * Validate a candidate file against a catalog entry's upload constraints
 * (apply-document-uploads Req 5). Returns the first violated constraint as a
 * `FieldValidationError`, or `null` when the file is acceptable.
 *
 * Rules, applied in order and only when the corresponding constraint is
 * declared on the entry:
 *   - `allowedContentTypes` present and `file.contentType` not in it →
 *     `FILE_TYPE`, naming the allowed content types (Req 5.1).
 *   - `maxFileSizeBytes` present and `file.sizeBytes` exceeds it → `FILE_SIZE`,
 *     naming the maximum allowed size (Req 5.2).
 *   - `maxFileCount` present and `existingCountForSlot + 1` exceeds it →
 *     `FILE_COUNT`, naming the maximum allowed count (Req 5.3).
 *
 * A constraint that is absent (null/undefined) is never enforced (Req 5.4).
 * The `fieldId` names the offending slot (the catalog entry id) so the row can
 * surface the reason. Pure + total; neither `entry` nor `file` is mutated, and
 * no network call is made — the caller rejects before requesting a presigned
 * upload URL (Req 5.5) and reuses this same check for replacement files (Req 6.4).
 *
 * @param entry               the target catalog slot and its constraints
 * @param file                the candidate file's content type and size
 * @param existingCountForSlot how many documents are already uploaded to this slot
 */
export function validateDocumentUpload(
  entry: DocumentCatalogEntry,
  file: CandidateUpload,
  existingCountForSlot: number,
): FieldValidationError | null {
  // Req 5.1 — content-type allowlist (enforced only when declared; Req 5.4).
  if (
    Array.isArray(entry.allowedContentTypes) &&
    entry.allowedContentTypes.length > 0 &&
    !entry.allowedContentTypes.includes(file.contentType)
  ) {
    return {
      fieldId: entry.id,
      code: 'FILE_TYPE',
      message: `File type ${file.contentType} is not allowed. Allowed types: ${entry.allowedContentTypes.join(
        ', ',
      )}.`,
    };
  }

  // Req 5.2 — maximum file size (enforced only when declared; Req 5.4).
  if (
    typeof entry.maxFileSizeBytes === 'number' &&
    file.sizeBytes > entry.maxFileSizeBytes
  ) {
    return {
      fieldId: entry.id,
      code: 'FILE_SIZE',
      message: `File exceeds the maximum allowed size of ${entry.maxFileSizeBytes} bytes.`,
    };
  }

  // Req 5.3 — maximum file count per slot (enforced only when declared; Req 5.4).
  if (
    typeof entry.maxFileCount === 'number' &&
    existingCountForSlot + 1 > entry.maxFileCount
  ) {
    return {
      fieldId: entry.id,
      code: 'FILE_COUNT',
      message: `This slot accepts at most ${entry.maxFileCount} file${
        entry.maxFileCount === 1 ? '' : 's'
      }.`,
    };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Scope

/**
 * Whether `entry`'s Division scope includes `divisionKey`. A null/undefined/empty
 * `visibleDivisionKeys` means all divisions (the `BOTH` semantics, reusing
 * `visibility.ts#isAllDivisionsVisibility`); a populated list is in scope iff it
 * contains the key.
 */
function entryInDivisionScope(entry: DocumentCatalogEntry, divisionKey: string): boolean {
  if (isAllDivisionsVisibility(entry.visibleDivisionKeys)) {
    return true;
  }
  return (entry.visibleDivisionKeys as string[]).some(
    (k) => typeof k === 'string' && k.trim() !== '' && k === divisionKey,
  );
}

/**
 * Whether `entry`'s applicant-type scope includes `applicantType`. A null/undefined
 * scope or `'BOTH'` means all applicant types; otherwise the scope must equal the
 * applicant's type.
 */
function entryInApplicantTypeScope(
  entry: DocumentCatalogEntry,
  applicantType: ApplicantType,
): boolean {
  const scope = entry.applicantTypeScope;
  if (scope == null || scope === 'BOTH') {
    return true;
  }
  return scope === applicantType;
}

/**
 * The catalog entries in scope for an applicant assigned to `divisionKey` with
 * the given `applicantType` (Req 20.2). An entry is in scope iff its Division
 * scope includes the applicant's Division AND its applicant-type scope includes
 * the applicant's type. Drives the uploader dropdown and the required checklist.
 * Preserves the catalog's order. Pure + total.
 */
export function entriesInScope(
  catalog: DocumentCatalogEntry[],
  divisionKey: string,
  applicantType: ApplicantType,
): DocumentCatalogEntry[] {
  return catalog.filter(
    (entry) =>
      entryInDivisionScope(entry, divisionKey) &&
      entryInApplicantTypeScope(entry, applicantType),
  );
}

// ---------------------------------------------------------------------------
// Phase derivation, labeling, and the Phase-2 upload gate
// (application-builder-documents Req 3, 5)

/**
 * The entry's effective phase (application-builder-documents Req 3.5, 3.6, 6.4).
 * A persisted `phase` wins; otherwise the phase is derived from
 * `requiredForSubmission` (`true ⇒ PHASE_1`, `false ⇒ PHASE_2`) so legacy rows
 * without a stored phase still resolve to a phase on every read and decision.
 * Total + pure; neither branch throws.
 */
export function effectivePhase(entry: {
  phase?: DocumentPhase | null;
  requiredForSubmission: boolean;
}): DocumentPhase {
  if (entry.phase === 'PHASE_1' || entry.phase === 'PHASE_2') {
    return entry.phase;
  }
  return entry.requiredForSubmission ? 'PHASE_1' : 'PHASE_2';
}

/**
 * The applicant/director-facing label for an entry's phase
 * (application-builder-documents Req 3.1–3.4). `PHASE_1 ⇒ "Phase 1"`,
 * `PHASE_2 ⇒ "Phase 2"`; never the legacy "Required"/"Supplemental" words.
 * Total + pure.
 */
export function phaseLabel(phase: DocumentPhase): 'Phase 1' | 'Phase 2' {
  return phase === 'PHASE_1' ? 'Phase 1' : 'Phase 2';
}

/**
 * The Phase-2 upload gate (application-builder-documents Req 5.2, 5.5, P5.1).
 * Denies an upload iff the target slot's effective phase is `PHASE_2` AND the
 * applicant's overall application is not yet approved
 * (`submissionStatus !== 'APPROVED'`). A Phase_1 slot is never denied by this
 * rule, and a Phase_2 slot is allowed once the application is `APPROVED`. This
 * is the authoritative server-side invariant, independent of any client hide.
 * Total + pure.
 */
export function isPhase2UploadDenied(
  entry: { phase?: DocumentPhase | null; requiredForSubmission: boolean },
  submissionStatus: string | null | undefined,
): boolean {
  return effectivePhase(entry) === 'PHASE_2' && submissionStatus !== 'APPROVED';
}

// ---------------------------------------------------------------------------
// Submission gate

/**
 * The submission gate (Req 20.3): the required-for-submission catalog entries
 * that are in scope for the applicant but have NO matching uploaded document.
 * A document matches a catalog entry by `catalogEntryId`. Submission is blocked
 * iff the returned list is non-empty; the list names exactly the unsatisfied
 * required in-scope slots. Preserves catalog order. Pure + total.
 *
 * @param catalog       the configured Document_Catalog
 * @param divisionKey   the applicant's Division key
 * @param applicantType the applicant's type
 * @param uploaded      the applicant's uploaded documents, matched by catalogEntryId
 */
export function missingRequiredDocuments(
  catalog: DocumentCatalogEntry[],
  divisionKey: string,
  applicantType: ApplicantType,
  uploaded: UploadedDocument[],
): DocumentCatalogEntry[] {
  const uploadedEntryIds = new Set(uploaded.map((u) => u.catalogEntryId));
  return entriesInScope(catalog, divisionKey, applicantType).filter(
    (entry) => entry.requiredForSubmission && !uploadedEntryIds.has(entry.id),
  );
}
